import { BaseDialog } from './BaseDialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  onConfirm: () => void
  onCancel?: () => void
  variant?: 'default' | 'destructive' | 'success' | 'warning'
  loading?: boolean
}

const variantConfig = {
  default: {
    icon: Info,
    confirmVariant: 'default' as const,
    iconColor: 'text-blue-500'
  },
  destructive: {
    icon: XCircle,
    confirmVariant: 'destructive' as const,
    iconColor: 'text-red-500'
  },
  success: {
    icon: CheckCircle,
    confirmVariant: 'default' as const,
    iconColor: 'text-green-500'
  },
  warning: {
    icon: AlertTriangle,
    confirmVariant: 'default' as const,
    iconColor: 'text-yellow-500'
  }
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Bekräfta',
  cancelText = 'Avbryt',
  onConfirm,
  onCancel,
  variant = 'default',
  loading = false
}: ConfirmDialogProps) {
  const config = variantConfig[variant]
  const Icon = config.icon

  const handleCancel = () => {
    onCancel?.()
    onOpenChange(false)
  }

  const handleConfirm = () => {
    onConfirm()
  }

  const footer = (
    <div className="flex gap-3 justify-end">
      <Button
        variant="outline"
        onClick={handleCancel}
        disabled={loading}
      >
        {cancelText}
      </Button>
      <Button
        variant={config.confirmVariant}
        onClick={handleConfirm}
        disabled={loading}
      >
        {confirmText}
      </Button>
    </div>
  )

  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={footer}
      size="sm"
    >
      <div className="flex items-center gap-4 py-4">
        <Icon className={`h-8 w-8 ${config.iconColor}`} />
        <div className="flex-1">
          {description && (
            <p className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      </div>
    </BaseDialog>
  )
}